import React, { useState } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { ArrowLeft, Search, MapPin, Shield, Users, ExternalLink, Info, CalendarClock, Mail, Phone, Building2, Globe } from "lucide-react";
import { STATES, EMAIL_A_PRISONER } from "../data/statePrisons";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const telHref = (p) => `tel:${p.replace(/[^0-9]/g, "")}`;

const StatePrisons = () => {
  const { state } = useParams();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const data = STATES[state];
  if (!data) return <Navigate to="/prisons" replace />;

  const filtered = data.prisons.filter(
    (p) =>
      p.name.toLowerCase().includes(q.toLowerCase()) ||
      (p.region || "").toLowerCase().includes(q.toLowerCase())
  );

  const bookOnline = selected?.bookOnline || (selected && data.bookOnlineAll);

  return (
    <div className="mx-auto max-w-4xl px-4 py-14 md:px-8">
      <button
        onClick={() => navigate("/prisons")}
        className="mb-8 inline-flex items-center gap-2 font-display text-sm uppercase tracking-wide text-[#e0574c] transition-colors hover:text-[#e0a82e]"
      >
        <ArrowLeft className="h-4 w-4" /> Back to States
      </button>

      <h1 className="font-display text-4xl font-700 uppercase tracking-wide text-[#e0a82e] md:text-5xl">
        {data.name} Prisons
      </h1>
      <p className="mt-3 text-[#c4c4c4]">
        A directory of {data.name} correctional centres, operated by {data.dept}. Tap a facility for its address and visit booking details.
      </p>

      <div className="relative mt-6">
        <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#8a8a8a]" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by facility or location..."
          className="w-full rounded border border-white/15 bg-[#242424] py-3 pl-12 pr-4 text-[#eee] outline-none transition-colors focus:border-[#e0a82e]"
        />
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        {filtered.map((p) => (
          <button
            key={p.name}
            onClick={() => setSelected(p)}
            className="rounded-md border border-white/10 bg-[#242424] p-5 text-left transition-colors hover:border-[#e0a82e]/50"
          >
            <h3 className="font-display text-lg font-600 uppercase tracking-wide text-[#f0f0f0]">{p.name}</h3>
            <div className="mt-3 space-y-1.5 text-sm text-[#bcbcbc]">
              <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#e0a82e]" /> {p.region}</p>
              {p.security && <p className="flex items-center gap-2"><Shield className="h-4 w-4 text-[#e0a82e]" /> {p.security}</p>}
              {p.gender && <p className="flex items-center gap-2"><Users className="h-4 w-4 text-[#e0a82e]" /> {p.gender}</p>}
            </div>
            <span className="mt-4 inline-flex items-center gap-1 font-display text-xs uppercase tracking-wide text-[#e0a82e]">
              <Info className="h-3.5 w-3.5" /> Address & booking
            </span>
          </button>
        ))}
        {filtered.length === 0 && (
          <p className="col-span-full py-8 text-center text-[#9a9a9a]">No facilities match your search.</p>
        )}
      </div>

      <a
        href={data.locateUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-8 inline-flex items-center justify-center gap-2 rounded bg-[#e0a82e] px-6 py-3 font-display text-sm font-600 uppercase tracking-wide text-[#1c1c1c] transition-colors hover:bg-[#f2b93f]"
      >
        Locate a Prisoner ({data.name}) <ExternalLink className="h-4 w-4" />
      </a>
      {data.locateNote && <p className="mt-3 text-xs leading-relaxed text-[#9a9a9a]">{data.locateNote}</p>}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-h-[88vh] max-w-lg overflow-y-auto border-white/10 bg-[#1f1f1f] text-[#e9e9e9]">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl font-700 uppercase tracking-wide text-[#e0a82e]">
                  {selected.name}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-1.5 text-sm text-[#c8c8c8]">
                {selected.security && <p className="flex items-center gap-2"><Shield className="h-4 w-4 text-[#e0a82e]" /> <span><span className="text-[#8f8f8f]">Security: </span>{selected.security}</span></p>}
                {selected.gender && <p className="flex items-center gap-2"><Users className="h-4 w-4 text-[#e0a82e]" /> <span><span className="text-[#8f8f8f]">Accommodates: </span>{selected.gender}</span></p>}
              </div>

              {/* FACILITY INFO */}
              <div className="rounded-md border border-white/10 bg-[#262626] p-4">
                <p className="flex items-center gap-2 font-display text-sm uppercase tracking-wide text-[#e0574c]">
                  <Building2 className="h-4 w-4" /> Facility Info
                </p>
                <div className="mt-3 space-y-2 text-sm text-[#d0d0d0]">
                  <p className="flex items-start gap-2">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#e0a82e]" />
                    <span><span className="text-[#8f8f8f]">Address: </span>{selected.address || `${selected.region} — confirm exact address via the official site`}</span>
                  </p>
                  {selected.phone && (
                    <p className="flex items-start gap-2">
                      <Phone className="mt-0.5 h-4 w-4 shrink-0 text-[#e0a82e]" />
                      <span><span className="text-[#8f8f8f]">General enquiries: </span><a href={telHref(selected.phone)} className="text-[#e0a82e] hover:underline">{selected.phone}</a></span>
                    </p>
                  )}
                  {selected.address && (
                    <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selected.name + " " + selected.address)}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-600 uppercase tracking-wide text-[#e0a82e] hover:underline">
                      <MapPin className="h-3.5 w-3.5" /> View on map
                    </a>
                  )}
                </div>
              </div>

              {/* BOOK A VISIT */}
              <div className="rounded-md border border-white/10 bg-[#262626] p-4">
                <p className="flex items-center gap-2 font-display text-sm uppercase tracking-wide text-[#e0574c]">
                  <CalendarClock className="h-4 w-4" /> Book a Visit
                </p>
                <div className="mt-3 space-y-2 text-sm text-[#d0d0d0]">
                  {selected.bookEmail && (
                    <p className="flex items-start gap-2"><Mail className="mt-0.5 h-4 w-4 shrink-0 text-[#e0a82e]" /><span><span className="text-[#8f8f8f]">Booking email: </span><a href={`mailto:${selected.bookEmail}`} className="break-all text-[#e0a82e] hover:underline">{selected.bookEmail}</a></span></p>
                  )}
                  {selected.bookPhone && (
                    <p className="flex items-start gap-2"><Phone className="mt-0.5 h-4 w-4 shrink-0 text-[#e0a82e]" /><span><span className="text-[#8f8f8f]">Booking phone: </span><a href={telHref(selected.bookPhone)} className="text-[#e0a82e] hover:underline">{selected.bookPhone}</a></span></p>
                  )}
                  {bookOnline && (
                    <p className="flex items-start gap-2"><Globe className="mt-0.5 h-4 w-4 shrink-0 text-[#e0a82e]" /><span><span className="text-[#8f8f8f]">Book online: </span><a href={bookOnline} target="_blank" rel="noopener noreferrer" className="break-all text-[#e0a82e] hover:underline">Booking portal</a></span></p>
                  )}
                  {selected.bookNote && <p className="text-[#bcbcbc]">{selected.bookNote}</p>}
                  {!selected.bookEmail && !selected.bookPhone && !bookOnline && !selected.bookNote && (
                    <p className="text-[#bcbcbc]">Bookings are handled via general enquiries above.</p>
                  )}
                  <p className="mt-2 text-xs leading-relaxed text-[#9a9a9a]">{data.bookingNote}</p>
                </div>
              </div>

              <p className="text-xs text-[#8a8a8a]">
                Details can change — confirm current visiting hours and requirements on the official {data.dept} site.
                {data.statewidePhone && <> Statewide: <a href={telHref(data.statewidePhone)} className="text-[#e0a82e] hover:underline">{data.statewidePhone}</a>.</>}
              </p>

              <div className="flex flex-wrap gap-2">
                <a href={data.visitsUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded border border-[#e0a82e]/60 px-4 py-2 font-display text-xs font-600 uppercase tracking-wide text-[#e0a82e] transition-colors hover:bg-[#e0a82e] hover:text-[#1c1c1c]"><Info className="h-4 w-4" /> Visiting Guide</a>
                <a href={data.locateUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded border border-[#e0a82e]/60 px-4 py-2 font-display text-xs font-600 uppercase tracking-wide text-[#e0a82e] transition-colors hover:bg-[#e0a82e] hover:text-[#1c1c1c]"><Search className="h-4 w-4" /> Locate</a>
                <a href={EMAIL_A_PRISONER} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded border border-[#e0a82e]/60 px-4 py-2 font-display text-xs font-600 uppercase tracking-wide text-[#e0a82e] transition-colors hover:bg-[#e0a82e] hover:text-[#1c1c1c]"><Mail className="h-4 w-4" /> Email a Prisoner</a>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StatePrisons;
